import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Activity, Gauge, MapPin, Route as RouteIcon, TrendingUp, Users } from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import MetricTile from '../../components/admin/MetricTile';
import Avatar from '../../components/ui/Avatar';
import Badge from '../../components/ui/Badge';
import Skeleton from '../../components/ui/Skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { getInsights } from '../../utils/api';
import { actionBadge } from '../../utils/commandCenterHelpers';

export default function InsightsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInsights();
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load insights');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totals = data?.totals;
  const maxDay = Math.max(1, ...(data?.savesPerDay || []).map((d) => d.count));

  return (
    <>
      <PageHeader
        eyebrow="Super Admin"
        title="Insights"
        subtitle="Activity trends, top contributors, and dataset coverage at a glance."
        icon={Gauge}
      />

      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3.5">
        <MetricTile icon={MapPin} label="Stops" value={loading ? '—' : totals?.stops ?? 0} accent="brand" />
        <MetricTile icon={RouteIcon} label="Routes" value={loading ? '—' : totals?.routes ?? 0} accent="violet" />
        <MetricTile icon={Users} label="Users" value={loading ? '—' : totals?.users ?? 0} accent="sky" />
        <MetricTile icon={Activity} label="Saves · 30d" value={loading ? '—' : totals?.savesLast30 ?? 0} accent="emerald" />
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5 items-start">
        {/* saves per day */}
        <Card>
          <CardHeader>
            <CardTitle>Save activity · last 30 days</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-44 rounded-lg" />
            ) : (
              <div className="flex items-end gap-[3px] h-44">
                {(data?.savesPerDay || []).map((d) => (
                  <div key={d.date} className="flex-1 group relative flex flex-col justify-end">
                    <div
                      className="w-full rounded-t bg-gradient-to-t from-brand-500 to-brand-400 transition-all hover:from-brand-600 hover:to-brand-500"
                      style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: d.count ? '4px' : '2px', opacity: d.count ? 1 : 0.25 }}
                    />
                    <span className="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-1.5 py-0.5 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      {d.date.slice(5)} · {d.count}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-2 flex justify-between text-[10.5px] text-slate-400">
              <span>30 days ago</span>
              <span>Today</span>
            </div>
          </CardContent>
        </Card>

        {/* top contributors */}
        <Card>
          <CardHeader>
            <CardTitle>Top contributors</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)}
              </div>
            ) : (data?.topContributors || []).length === 0 ? (
              <p className="text-[12.5px] text-slate-400 py-4 text-center">No contributions yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {data.topContributors.map((c, i) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/command-center/users/${c.id}`)}
                      className="w-full flex items-center gap-2.5 p-2 rounded-lg hover:bg-slate-50 transition-colors text-left"
                    >
                      <span className="text-[12px] font-semibold text-slate-400 w-4 tabular-nums">{i + 1}</span>
                      <Avatar name={c.name} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-medium text-slate-900 truncate">{c.name}</p>
                        <p className="text-[11px] text-slate-400 truncate">@{c.username}</p>
                      </div>
                      <Badge variant="brand" size="sm" className="tabular-nums">{c.saves} saves</Badge>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* action breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Change actions</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-28 rounded-lg" />
            ) : (
              <div className="space-y-2.5">
                {['create', 'update', 'import', 'rollback', 'baseline'].map((action) => {
                  const count = data?.actionBreakdown?.[action] || 0;
                  const total = Object.values(data?.actionBreakdown || {}).reduce((a, b) => a + b, 0) || 1;
                  const badge = actionBadge(action);
                  return (
                    <div key={action} className="flex items-center gap-3">
                      <Badge variant={badge.variant} size="sm" className="w-24 justify-center">{badge.label}</Badge>
                      <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-brand-500" style={{ width: `${(count / total) * 100}%` }} />
                      </div>
                      <span className="text-[12.5px] font-semibold tabular-nums text-slate-700 w-10 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* coverage */}
        <Card>
          <CardHeader>
            <CardTitle>Route coverage</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-28 rounded-lg" />
            ) : (
              <>
                <div className="flex items-baseline gap-2">
                  <span className="text-[28px] font-semibold text-slate-900 tabular-nums">{data?.avgStopsPerRoute ?? 0}</span>
                  <span className="text-[12.5px] text-slate-500">avg stops per route</span>
                </div>
                <div className="mt-3 space-y-2">
                  {Object.entries(data?.routesByDirection || {}).map(([dir, count]) => (
                    <div key={dir} className="flex items-center justify-between text-[13px]">
                      <span className="text-slate-600">{dir}</span>
                      <Badge variant="outline" size="sm" className="tabular-nums">{count} routes</Badge>
                    </div>
                  ))}
                  {Object.keys(data?.routesByDirection || {}).length === 0 && (
                    <p className="text-[12.5px] text-slate-400">No routes yet.</p>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
